import { X } from "lucide-react";
import { type CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import { cn } from "~/lib/utils";

export interface PopupConfig {
  enabled: boolean;
  frequency: "session" | "always" | "once";
  title: string;
  description: string;
  image: string;
  background_image: string;
  background_color: string;
  cta: { label: string; url: string; blank: boolean } | null;
}

const STORAGE_KEY = "chorus-site-popup-seen";

function hasContent(config: PopupConfig): boolean {
  return Boolean(
    config.title || config.description || config.image || config.cta
  );
}

/** Legge lo stato "già visto" in base alla frequenza configurata. */
function alreadySeen(frequency: PopupConfig["frequency"]): boolean {
  if (frequency === "always") return false;
  try {
    const store =
      frequency === "once" ? window.localStorage : window.sessionStorage;
    return store.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markSeen(frequency: PopupConfig["frequency"]): void {
  if (frequency === "always") return;
  try {
    const store =
      frequency === "once" ? window.localStorage : window.sessionStorage;
    store.setItem(STORAGE_KEY, "1");
  } catch {
    /* storage non disponibile: ignora */
  }
}

/**
 * Popup configurabile mostrato all'apertura del sito.
 *
 * Design (vedi layout brand): card QUADRATA bordeaux (secondary) con immagine
 * di sfondo in cover (la texture a onde), testo bianco, immagine centrale
 * (es. il premio) tra titolo e descrizione, X di chiusura in un quadrato crema
 * (white-nude) e CTA crema con testo bordeaux.
 *
 * Usa il Dialog Radix/shadcn del progetto: focus-trap, ripristino del focus,
 * Esc e blocco scroll sono gestiti dal componente. Renderizza `null` lato
 * server e finché non decide (client, in base a enabled/frequency) di aprirsi:
 * nessun mismatch di hydration né flash di contenuto.
 */
export function SitePopup({ config }: { config?: PopupConfig | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!config || !config.enabled || !hasContent(config)) return;
    if (alreadySeen(config.frequency)) return;
    markSeen(config.frequency);
    setOpen(true);
  }, [config]);

  if (!config) return null;

  // Sfondo: immagine in cover (precedenza) o colore. Se nessuno dei due, resta
  // il bordeaux del brand (classe bg-secondary).
  const cardStyle: CSSProperties = config.background_image
    ? {
      backgroundImage: `url('${config.background_image}')`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }
    : config.background_color
      ? { backgroundColor: config.background_color }
      : {};

  // Versione plain-text del titolo (può contenere HTML dal WYSIWYG) usata come
  // nome accessibile del dialog.
  const titlePlain = config.title
    ? config.title.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setOpen(false);
      }}
    >
      <DialogContent
        hideCloseButton
        className="!flex w-[92vw] max-w-lg flex-col gap-0 overflow-hidden border-0 bg-secondary p-0 shadow-2xl"
        style={cardStyle}
      >
        <DialogClose
          aria-label={t("Chiudi")}
          className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center bg-white-nude text-secondary transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white/60"
        >
          <X size={22} strokeWidth={1.75} />
          <span className="sr-only">{t("Chiudi")}</span>
        </DialogClose>

        <div
          className={cn(
            "flex flex-1 flex-col overflow-y-auto px-8 pt-14 pb-10 sm:px-10 text-center text-white",
            config.image ? "justify-between" : "justify-center gap-6"
          )}
        >
          {/* Titolo accessibile (Radix richiede sempre un DialogTitle). */}
          <DialogTitle className="sr-only">{titlePlain || "Popup"}</DialogTitle>

          {config.title && (
            <div
              className="font-red-hat font-normal uppercase leading-tight tracking-wide text-white text-xl sm:text-2xl [&_p]:m-0 [&_strong]:text-2xl [&_strong]:sm:text-3xl [&_strong]:font-bold"
              dangerouslySetInnerHTML={{ __html: config.title }}
            />
          )}

          {config.image && (
            <img
              src={config.image}
              alt=""
              className="mx-auto my-4 lg:min-h-0 w-auto object-contain min-h-40 lg:h-52"
            />
          )}

          <div>
            {config.description && (
              <DialogDescription
                className="text-white text-lg sm:text-xl uppercase tracking-wide [&_p]:m-0 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: config.description }}
              />
            )}

            {config.cta && (
              <a
                href={config.cta.url}
                target={config.cta.blank ? "_blank" : undefined}
                rel={config.cta.blank ? "noopener noreferrer" : undefined}
                className="mt-6 inline-block bg-white-nude px-16 py-3.5 text-lg font-bold uppercase tracking-[0.2em] text-secondary transition-opacity hover:opacity-90"
              >
                {config.cta.label}
              </a>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
